import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { fmt2 } from "@/lib/utils";
import { useLocations } from "@/context/LocationContext";
import { toast } from "sonner";
import { Plus, Printer, Trash2, AlertOctagon, PackageX } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { PageHeader, Panel, StaffInitials } from "@/components/ui-bits";
import LabelDialog from "@/components/LabelDialog";
import PackingWasteDialog from "@/components/packing/PackingWasteDialog";
import DateScopePicker, { scopeToParams, downloadCsv } from "@/components/DateScopePicker";

export default function Packing() {
  const { params, selected } = useLocations();
  const [rows, setRows] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ batch_id: "", pack_unit: "unit", packs_produced: 0 });
  const [labelFor, setLabelFor] = useState(null);
  const [wasteFor, setWasteFor] = useState(null);
  const [wasteForm, setWasteForm] = useState({ quantity: 1, reason: "Packing waste", notes: "" });
  const [scope, setScope] = useState({ mode: "today" });

  const load = async () => {
    const [p, b] = await Promise.all([
      api.get("/packing", {
        params: {
          ...params(),
          ...scopeToParams(scope),
        },
      }),
      // Only batches with NO packing row yet — a batch can be packed once.
      api.get("/batches", { params: { ...params(), unpacked_only: 1 } }),
    ]);
    setRows(p.data);
    setBatches(
      b.data.filter((x) =>
        ["complete", "packing", "packed", "baking"].includes(x.stage),
      ),
    );
  };
  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selected, scope]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.batch_id) {
      toast.error("Pick a batch");
      return;
    }
    try {
      await api.post("/packing", {
        ...form,
        packs_produced: Number(form.packs_produced),
      });
      toast.success("Packing recorded");
      setOpen(false);
      setForm({ batch_id: "", pack_unit: "unit", packs_produced: 0 });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const submitWaste = async (e) => {
    e.preventDefault();
    if (!wasteFor) return;
    try {
      await api.post(`/packing/${wasteFor.id}/waste`, {
        quantity: Number(wasteForm.quantity),
        reason: wasteForm.reason,
        notes: wasteForm.notes,
      });
      toast.success(`${wasteForm.quantity} pack${wasteForm.quantity === "1" ? "" : "s"} sent to waste`);
      setWasteFor(null);
      setWasteForm({ quantity: 1, reason: "Packing waste", notes: "" });
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record waste");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete packing record?")) return;
    await api.delete(`/packing/${id}`);
    load();
  };

  // "Record unsold" — manager logs how many packs went unsold at EOD
  // (binned/donated/staff). Doesn't touch `remaining_packs`; feeds the
  // Carry-over panel on Production so bakers can trim tomorrow's plan.
  const [unsoldFor, setUnsoldFor] = useState(null);
  const [unsoldForm, setUnsoldForm] = useState({ unsold_packs: 0, notes: "" });
  const openUnsold = (p) => {
    setUnsoldFor(p);
    setUnsoldForm({
      unsold_packs: Number(p.unsold_packs || 0),
      notes: p.unsold_notes || "",
    });
  };
  const submitUnsold = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/packing/${unsoldFor.id}/unsold`, {
        unsold_packs: Number(unsoldForm.unsold_packs || 0),
        notes: unsoldForm.notes,
      });
      toast.success(
        `${unsoldForm.unsold_packs} pack${Number(unsoldForm.unsold_packs) === 1 ? "" : "s"} logged as unsold`,
      );
      setUnsoldFor(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to record unsold");
    }
  };

  // When the user picks a batch in the manual-packing dialog, prefill the
  // packs_produced field from the batch's recorded yield.
  const onBatchPick = (bid) => {
    const b = batches.find((x) => x.id === bid);
    setForm((f) => ({
      ...f,
      batch_id: bid,
      packs_produced: b?.quantity_produced
        ? Math.round(b.quantity_produced)
        : f.packs_produced,
    }));
  };

  return (
    <div>
      <PageHeader title="Packing & Labels" eyebrow="Production · Final step">
        <Button className="h-12 px-5 gap-2" onClick={() => setOpen(true)} data-testid="new-packing-button">
          <Plus className="h-5 w-5" /> Record packing
        </Button>
      </PageHeader>

      <Panel
        title={`Packing records (${rows.length})`}
        action={
          <div className="flex items-center gap-2 flex-wrap">
            <DateScopePicker value={scope} onChange={setScope} />
            {scope.mode === "history" && rows.length > 0 && (
              <Button
                size="sm"
                variant="outline"
                onClick={() =>
                  downloadCsv(
                    `packing-${scope.from_date}-to-${scope.to_date}.csv`,
                    rows,
                    [
                      { header: "Label code", value: "label_code" },
                      { header: "Batch code", value: "batch_code" },
                      { header: "Recipe", value: "recipe_name" },
                      { header: "Packs produced", value: "packs_produced" },
                      { header: "Remaining packs", value: "remaining_packs" },
                      { header: "Pack unit", value: "pack_unit" },
                      { header: "Packed at", value: "packed_at" },
                      { header: "Packed by", value: "packed_by_name" },
                    ]
                  )
                }
                data-testid="packing-export-csv"
              >
                Export CSV
              </Button>
            )}
          </div>
        }
      >
        {rows.length === 0 ? (
          <div className="py-10 text-center text-stone-500">
            No packing records yet. Complete a production batch and then use
            &quot;+Record packing&quot; to add a run.
          </div>
        ) : (
          <table className="w-full" data-testid="packing-table">
            <thead className="text-left text-xs uppercase tracking-wider text-stone-500">
              <tr>
                <th className="py-3">Label</th>
                <th>From batch</th>
                <th>Product</th>
                <th className="text-right">Packs (remaining / made)</th>
                <th className="text-right">Waste</th>
                <th>Packed at</th>
                <th>By</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id} className="border-t border-border h-16" data-testid={`packing-row-${p.id}`}>
                  <td className="font-mono text-sm">
                    {p.label_code}
                    {p.auto_created && (
                      <span
                        className="ml-2 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-300 font-bold"
                        title="Auto-created on batch complete"
                      >
                        Auto
                      </span>
                    )}
                  </td>
                  <td className="font-mono text-sm text-stone-600 dark:text-stone-300">{p.batch_code}</td>
                  <td>{p.recipe_name}</td>
                  <td className="text-right tnum">
                    <span className="font-semibold">{fmt2(p.remaining_packs)}</span>
                    <span className="text-stone-500"> / {fmt2(p.packs_produced)} {p.pack_unit}</span>
                  </td>
                  <td className="text-right tnum">
                    {p.packing_waste ? (
                      <span className="text-rose-700 dark:text-rose-300 font-semibold">{p.packing_waste}</span>
                    ) : (
                      <span className="text-stone-400">0</span>
                    )}
                  </td>
                  <td className="text-stone-600 dark:text-stone-300 text-sm">
                    {p.packed_at?.slice(0, 16).replace("T", " ")}
                  </td>
                  <td>
                    <StaffInitials name={p.packed_by_name} testId={`packing-by-${p.id}`} />
                  </td>
                  <td className="flex gap-1 justify-end items-center h-16">
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => openUnsold(p)}
                      data-testid={`pack-unsold-${p.id}`}
                      title="Record unsold packs (binned / donated / carry-over)"
                    >
                      <PackageX
                        className={`h-4 w-4 ${p.unsold_packs ? "text-amber-700" : "text-stone-500"}`}
                      />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setWasteFor(p)}
                      data-testid={`pack-waste-${p.id}`}
                      title="Record packing waste"
                      disabled={!p.remaining_packs}
                    >
                      <AlertOctagon className="h-4 w-4 text-rose-600" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => setLabelFor(p)}
                      data-testid={`print-label-${p.id}`}
                    >
                      <Printer className="h-4 w-4" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      onClick={() => remove(p.id)}
                      data-testid={`delete-packing-${p.id}`}
                    >
                      <Trash2 className="h-4 w-4 text-rose-600" />
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="packing-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Record packing run</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div>
              <Label>From batch</Label>
              <Select value={form.batch_id} onValueChange={onBatchPick}>
                <SelectTrigger className="h-12" data-testid="packing-batch">
                  <SelectValue placeholder="Pick batch" />
                </SelectTrigger>
                <SelectContent>
                  {batches.map((b) => (
                    <SelectItem key={b.id} value={b.id}>
                      {b.batch_code} · {b.recipe_name} ({b.quantity_produced || b.quantity_planned})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Packs produced</Label>
                <Input
                  type="number"
                  min="0"
                  className="h-12 mt-1"
                  value={form.packs_produced}
                  onChange={(e) => setForm({ ...form, packs_produced: e.target.value })}
                  required
                  data-testid="packing-count"
                />
              </div>
              <div>
                <Label>Pack unit</Label>
                <Input
                  className="h-12 mt-1"
                  value={form.pack_unit}
                  onChange={(e) => setForm({ ...form, pack_unit: e.target.value })}
                />
              </div>
            </div>
            <p className="text-xs text-stone-500">
              Tip: when a production batch is completed it auto-creates a packing record using the
              produced quantity — you&apos;ll usually only land here for an extra label run.
            </p>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" className="h-12 px-6" data-testid="packing-submit">
                Save packing
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <PackingWasteDialog
        wasteFor={wasteFor}
        wasteForm={wasteForm}
        setWasteForm={setWasteForm}
        onClose={() => setWasteFor(null)}
        onSubmit={submitWaste}
      />

      <LabelDialog packing={labelFor} onClose={() => setLabelFor(null)} />

      <Dialog open={!!unsoldFor} onOpenChange={(o) => !o && setUnsoldFor(null)}>
        <DialogContent data-testid="unsold-dialog">
          <DialogHeader>
            <DialogTitle className="font-display">Record unsold packs</DialogTitle>
          </DialogHeader>
          {unsoldFor && (
            <form onSubmit={submitUnsold} className="space-y-3">
              <div className="text-sm text-stone-600 dark:text-stone-300">
                <div>
                  <span className="font-mono">{unsoldFor.batch_code}</span>
                  {" · "}
                  {unsoldFor.recipe_name}
                </div>
                <div className="text-xs">
                  On hand: <span className="font-bold">{unsoldFor.remaining_packs}</span> of{" "}
                  {unsoldFor.packs_produced} {unsoldFor.pack_unit}
                </div>
              </div>
              <div>
                <Label>Unsold packs (binned / donated / carry-over)</Label>
                <Input
                  type="number"
                  min="0"
                  step="1"
                  className="h-12 mt-1"
                  value={unsoldForm.unsold_packs}
                  onChange={(e) =>
                    setUnsoldForm({ ...unsoldForm, unsold_packs: e.target.value })
                  }
                  data-testid="unsold-count"
                  autoFocus
                />
                <p className="text-xs text-stone-500 mt-1">
                  Reduces on-hand inventory by this amount. Use for bread
                  that&apos;s gone (binned, donated, staff, carry-over).
                  Raising or lowering later adjusts inventory accordingly.
                </p>
              </div>
              <div>
                <Label>Notes (optional)</Label>
                <Input
                  className="h-12 mt-1"
                  value={unsoldForm.notes}
                  onChange={(e) =>
                    setUnsoldForm({ ...unsoldForm, notes: e.target.value })
                  }
                  placeholder="e.g. donated to shelter"
                  data-testid="unsold-notes"
                />
              </div>
              <DialogFooter>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setUnsoldFor(null)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  className="h-12 px-6"
                  data-testid="unsold-submit"
                >
                  Save unsold
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
